import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { api, setStoredToken, setUnauthorizedHandler } from "./api";
import type { AuthMeDTO } from "../shared/dto";

interface AuthCtx {
  loading: boolean;
  me: AuthMeDTO | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AuthMeDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // セッション切れ (401) を検知したら、認証状態を破棄してログイン画面へ遷移する。
  // RequireAuth 配下のページは me=null で /login へ。明示遷移も行い確実に画面を切り替える。
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setStoredToken(null);
      setMe(null);
      navigate("/login", { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<AuthMeDTO>("/auth/me");
      setMe(data);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      await api.post("/auth/login", { email, password });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setStoredToken(null); // LIFF 用 Bearer トークンも破棄
    setMe(null);
  }, []);

  const value = useMemo(
    () => ({ loading, me, refresh, login, logout }),
    [loading, me, refresh, login, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider missing");
  return v;
}
